/**
 * The Help centre's sections, in index order (docs/help-centre.md). Owner: slice 36 `web-help-centre`.
 *
 * A guide lives at content/help/<section>/<slug>.md, and its frontmatter `section` must be one of these slugs and its
 * `audience` must equal the section's. Contractor sections are named `contractor-*`: the loader picks them out by that
 * folder name, so staff guides are never in the bundle chunk a contractor or a signed-out reader downloads.
 *
 * This module imports nothing, so scripts/help-freshness.ts can load it outside Vite.
 */
export type HelpAudience = 'staff' | 'contractor'

export interface HelpSection {
  slug: string
  title: string
  description: string
  audience: HelpAudience
}

export const HELP_SECTIONS = [
  {
    slug: 'getting-started',
    title: 'Getting started',
    description: 'Sign in, connect Desk and find your way around Patchbay.',
    audience: 'staff',
  },
  {
    slug: 'working-in-desk',
    title: 'Coming from Desk',
    description: 'You can keep working in Desk. What Patchbay adds, and how your Desk notes reach it.',
    audience: 'staff',
  },
  {
    slug: 'queue-and-cases',
    title: 'The queue and cases',
    description: 'Home, lanes, the case page, replying, closing, snoozing, follow-ups, proposals, new cases and calls.',
    audience: 'staff',
  },
  {
    slug: 'riley',
    title: 'Working with Riley',
    description: 'The Riley rail, asking Riley, its drafts, and the cards it proposes.',
    audience: 'staff',
  },
  {
    slug: 'dispatch-and-billing',
    title: 'Dispatch, ledger and billing',
    description: 'The schedule, booking a visit, the work timer, the ledger, and getting work ready to bill.',
    audience: 'staff',
  },
  {
    slug: 'projects',
    title: 'Projects',
    description: 'The portfolio, a project plan, reports, templates, and the tasks a technician sees.',
    audience: 'staff',
  },
  {
    slug: 'admin',
    title: 'Admin',
    description: 'People, settings, integrations, snippets, contractors, Riley operations and the audit log.',
    audience: 'staff',
  },
  {
    slug: 'contractor-portal',
    title: 'For contractors',
    description: 'Open your job link, work the ticket, check in and out, log time and send your invoice.',
    audience: 'contractor',
  },
] as const satisfies readonly HelpSection[]

export type HelpSectionSlug = (typeof HELP_SECTIONS)[number]['slug']
export const HELP_SECTION_SLUGS: readonly HelpSectionSlug[] = HELP_SECTIONS.map((s) => s.slug)

export function helpSection(slug: string): HelpSection | null {
  return HELP_SECTIONS.find((s) => s.slug === slug) ?? null
}

/** Contractor sections are the `contractor-*` folders (see the module comment). */
export function isContractorFolder(folder: string): boolean {
  return folder.startsWith('contractor-')
}

/** The URL of a guide; `id` is "<section>/<slug>". */
export function helpHref(id: string, anchor?: string): string {
  return `/help/${id}${anchor ? `#${anchor}` : ''}`
}
